

import { TextureLoader, Texture, WebGLRenderer, WebGLRenderTarget, OrthographicCamera, PerspectiveCamera, CompressedTexture, CubeTexture, DoubleSide, MeshBasicMaterial, NoBlending, Scene, PlaneGeometry, Mesh, EquirectangularReflectionMapping, CubeUVReflectionMapping, SRGBColorSpace, NoColorSpace } from "three";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader";
import { IValueConverter, ValueConverterOptions, ValueConversionService } from "../../properties/common/converter";
import { getBasisTranscoderPath, getTextureSourceSize, isCompressedTexture, isCubeTexture, isDataTexture, isTexture, isWebGLRenderTarget, ktx2Loader, loadTexture, textureTypeFromUrl } from "./helper";
import { isEnvironmentProjection } from "./helper.textures.js";
import { readbackRenderTargetToUrl } from "./helper.readback";
import { _runWhenIdle, Queue, runWhenGlobalIdle, TaskQueue, Throttler, timeout } from "../../../base/common/async";
import type { ThreeJs } from "./webgl.js";
import { Disposable } from "../../../base/common/lifecycle";
import { shallowCloneObject } from "../../../base/common/helpers";
import { ILogService } from "../../log/common/log";
import { IFileService } from "../../files/common/files";
import { URI } from "../../../base/common/uri";
import { BugIndicatingError } from "../../../base/common/errors";
import { HierarchyNode } from "../../hierarchy/common/hierarchyService";
import { NodeFlags } from "../../hierarchy/common/hierarchy";



// #region Texture Converter

/** Prefiltered environment maps (PMREM) packed as KTX2, e.g. `foo.pmrem.ktx2`. */
function isPmremUrl(url: string): boolean {
    return /\.pmrem\.ktx2(\?|$)/i.test(url) || /[?&]pmrem(=|&|$)/i.test(url);
}

/** The source string's filename, for texture display names (the loaded url is
 *  often a blob whose pathname is only a uuid — panels would show guids). */
function filenameFromSrc(src: string): string {
    const path = src.split(/[?#]/)[0];
    return path.substring(path.lastIndexOf("/") + 1);
}

interface TextureBlobCacheEntry {
    blobUrl: string;
    version: number;
    width: number;
    height: number;
}

type ImageSourceWithUrl = {
    src?: string;
    currentSrc?: string;
};

export class TextureConverter extends Disposable implements IValueConverter {

    /** All editor-initiated GPU work is serialized on the engine's shared queue
     * (fallback for tests / partial engine fakes without one). */
    private _fallbackQueue: TaskQueue | null = null;
    private get queue(): TaskQueue { return this.three?.gpuWorkQueue ?? (this._fallbackQueue ??= new TaskQueue()); }
    /** Cache for URL string → Texture. WEAK values (weak-only memory rule): the cache
     * dedups loads while the texture is in use anywhere, but never keeps a texture
     * alive on its own — deleted models' textures become collectable (this Map holding
     * textures STRONGLY was a confirmed leak: pmrem/provider textures survived delete). */
    private readonly stringToTextureCache = new Map<string, WeakRef<Texture>>();

    private getCachedTexture(url: string): Texture | undefined {
        const ref = this.stringToTextureCache.get(url);
        if (!ref) return undefined;
        const texture = ref.deref();
        if (!texture) this.stringToTextureCache.delete(url); // prune dead entry
        return texture;
    }
    /** Cache for Texture → blob URL (uses WeakMap so entries are GC'd when textures are disposed) */
    private readonly textureToBlobCache = new WeakMap<Texture, TextureBlobCacheEntry>();

    constructor(
        private readonly three: ThreeJs,
        @ILogService private readonly logger: ILogService,
        @IFileService private readonly fileService: IFileService,
    ) {
        super();
    }

    /** three's loaders fetch() their url — the editor's virtual schemes
     * (tmp:, file:) are unreachable that way ('URL scheme "tmp" is not
     * supported'). Read those through the FILE SERVICE and hand the loader a
     * transient blob url; browser-loadable urls (and scheme-less relatives)
     * pass through untouched. Callers revoke() once the loader has fetched. */
    async resolveLoadableUrl(src: string): Promise<{ url: string; revoke: () => void } | null> {
        const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(src)?.[1]?.toLowerCase();
        if (!scheme || scheme === "http" || scheme === "https" || scheme === "data" || scheme === "blob") {
            return { url: src, revoke: () => { } };
        }
        try {
            const content = await this.fileService.readFile(URI.parse(src));
            const blobUrl = URL.createObjectURL(new Blob([content.value.buffer as BlobPart]));
            return { url: blobUrl, revoke: () => URL.revokeObjectURL(blobUrl) };
        }
        catch (error) {
            this.logger.warn(`TextureConverter: could not read '${src}' through the file service`, error);
            return null;
        }
    }

    dispose(): void {
        super.dispose();
        this.stringToTextureCache.clear();
        this._pmremLoader?.dispose();
        this._pmremLoader = null;
        // WeakMap doesn't need clearing - entries are automatically removed when textures are GC'd
    }

    canConvert(value: unknown, context: ValueConverterOptions): boolean {
        if (typeof value === "string") {
            const str = value as string;
            const source = context.sourceName ?? str;
            if (str.startsWith("data:image/") || source.match(/\.(png|jpg|jpeg|gif|bmp|webp|svg|exr|hdr|hdri|ktx2)(\?|$)/i)) {
                if (context.targetType === "texture") return true;
                if (context.targetType === "blob:url") return true;
            }
        }
        else if (isTexture(value)) {
            if (context.targetType === "texture") return true;
            if (context.targetType === "blob:url") return true;
        }
        return false;
    }

    async convert<T extends unknown>(value: unknown, _options: ValueConverterOptions): Promise<T | null> {

        if (!value) {
            throw new BugIndicatingError("TextureConverter: Cannot convert null or undefined value.");
        }

        const options = { ..._options };

        return this.queue.schedule(async () => {

            if (isTexture(value) && options.targetType === "texture") {
                return value as unknown as T;
            }

            // idle-or-500ms: busy pages can starve requestIdleCallback forever,
            // which would stall the shared GPU queue (and every preview behind it)
            await new Promise(res => runWhenGlobalIdle(res, 500));

            if(_options.token?.isCancellationRequested) {
                this.logger.warn("TextureConverter: Conversion cancelled before start.");
                return null;
            }

            // The target is a texture
            if (options.targetType === "texture") {
                if (typeof value === "string") {
                    const str = value as string;
                    const sourceName = options.sourceName ?? str;
                    if (str.startsWith("data:image/") || sourceName.match(/\.(png|jpg|jpeg|gif|bmp|webp|svg|exr|hdr|hdri|ktx2)(\?|$)/i)) {
                        const cached = this.getCachedTexture(value);
                        if (cached) {
                            return cached as unknown as T;
                        }
                        // virtual-scheme srcs (tmp:, file:) become blob urls;
                        // loader choice keys on the ORIGINAL name's extension
                        const resolved = await this.resolveLoadableUrl(str);
                        if (!resolved) return null;
                        try {
                            // Engine-ready PMREM environments (KTX2) hold a prefiltered mip chain
                            // and must use CubeUVReflectionMapping. Loading them as a plain
                            // equirectangular texture would look wrong, so route them to the KTX2 loader.
                            if (options.environmentFormat === "pmrem" || isPmremUrl(sourceName)) {
                                const texture = await this.loadPmremTexture(resolved.url);
                                if (texture) {
                                    // resolved.url is a blob (uuid pathname) —
                                    // name from the SOURCE so panels don't show guids
                                    if (!texture.name) texture.name = filenameFromSrc(sourceName);
                                    this.stringToTextureCache.set(value, new WeakRef(texture));
                                }
                                return texture as unknown as T;
                            }
                            const texture = await loadTexture(resolved.url, textureTypeFromUrl(sourceName));
                            if(texture) {
                                if (!texture.name) texture.name = filenameFromSrc(sourceName);
                                this.stringToTextureCache.set(value, new WeakRef(texture));
                                switch(options.usageHint) {
                                    case "skybox":
                                        texture.mapping = EquirectangularReflectionMapping;
                                        break;
                                }
                            }
                            return texture as unknown as T;
                        }
                        catch (e) {
                            console.error("Failed to load texture from string:", e);
                        }
                        finally {
                            resolved.revoke();
                        }
                    }
                }
                else {
                    this.logger.warn("TextureConverter: Cannot convert value to texture (value type is " + typeof value + ")");
                }
            }
            // The target type
            else if (options.targetType === "blob:url") {

                if (isTexture(value)) {
                    const texture = value as Texture;
                    const requestedWidth = options.width || 0;
                    const requestedHeight = options.height || 0;

                    // Check WeakMap cache - entries are automatically cleaned up when textures are GC'd
                    const cached = this.textureToBlobCache.get(texture);
                    if (cached &&
                        cached.version === texture.version &&
                        cached.width === requestedWidth &&
                        cached.height === requestedHeight) {
                        return cached.blobUrl as unknown as T;
                    }

                    const blobUrl = await this.convertTextureToBlobUrl(texture, options);
                    if (blobUrl) {
                        this.textureToBlobCache.set(texture, {
                            blobUrl,
                            version: texture.version,
                            width: requestedWidth,
                            height: requestedHeight,
                        });
                        return blobUrl as unknown as T;
                    }
                    else {
                        console.warn("TextureConverter: Failed to convert texture to blob url.", texture.type);
                    }
                }
                else {
                    throw new BugIndicatingError(`TextureConverter: Cannot convert value to blob:url (value type is ${typeof value})`);
                }
            }

            // throw new BugIndicatingError(`TextureConverter: Cannot convert value to target type ${targetType} (value type is ${typeof value})`);

            return null;
        })
    }

    private _pmremLoader: KTX2Loader | null = null;

    /**
     * Load a prefiltered environment map (PMREM/KTX2) using three's KTX2Loader directly.
     *
     * We intentionally do NOT use @needle-tools/engine's loadPMREM here: this converter
     * also runs in the Chrome inspector, which operates on arbitrary pages that do not
     * ship Needle Engine. This mirrors the loader setup used by the engine source
     * (and windows.inspect.ts) - transcode via KTX2Loader, then apply CubeUV mapping.
     */
    private async loadPmremTexture(url: string): Promise<Texture | null> {
        const renderer = this.three.getRenderer<WebGLRenderer>();
        if (!renderer) {
            this.logger.warn("TextureConverter: No renderer available to load PMREM environment " + url);
            return null;
        }
        const loader = this._pmremLoader ??= new KTX2Loader()
            .setTranscoderPath(getBasisTranscoderPath());
        // detectSupport picks the transcode target from the renderer's capabilities.
        loader.detectSupport(renderer);
        try {
            const texture = await loader.loadAsync(url);
            // Prefiltered environment maps use CubeUV mapping, not equirectangular.
            texture.mapping = CubeUVReflectionMapping;
            return texture;
        }
        catch (e) {
            this.logger.warn("TextureConverter: Failed to load PMREM environment " + url, e);
            return null;
        }
    }

    private async convertTextureToBlobUrl(texture: Texture, options: ValueConverterOptions): Promise<string | null> {

        const renderer = this.three.findRendererForTexture(texture);
        if (renderer) {
            // cube textures render as an environment projection here — the
            // per-face canvas below is only the no-renderer fallback (and
            // can't read GPU-only/compressed faces at all)
            const tex = await this.renderTextureWithRenderer(texture, renderer, options);
            if (tex) {
                return tex;
            }
        }
        else {
            this.logger.warn("TextureConverter: No renderer found for texture " + texture.type + " " + texture.name);
        }

        if (isCubeTexture(texture)) {
            const dataUrl = this.renderCubeTextureToCanvas(texture, options);
            if (dataUrl) {
                return dataUrl;
            }
        }

        // STOP here for a prefiltered environment. Everything below draws the
        // texture as a flat image, and a PMREM atlas drawn flat is a picture of
        // the packing — six faces and a descending mip chain on black. That is
        // what the inspector showed for a scene's background and environment
        // whenever the environment render above did not produce a url: it does
        // not fail visibly, it silently falls through to here.
        if (isEnvironmentProjection(texture)) {
            this.logger.warn(
                `TextureConverter: no preview for prefiltered environment "${texture.name || texture.type}" — `
                + "the environment render produced nothing and a flat blit would show the atlas.",
            );
            return null;
        }


        const image = texture.image;
        const source = texture.source;

        const directSourceUrl =
            this.getDirectImageUrl(image) ||
            this.getDirectImageUrl(source?.data);
        if (directSourceUrl) {
            return directSourceUrl;
        }


        if (isDataTexture(texture)) {
            const width = image.width;
            const height = image.height;
            const data = image.data;
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                const imageData = ctx.createImageData(width, height);
                imageData.data.set(data);
                ctx.putImageData(imageData, 0, 0);
                return canvas.toDataURL()
            }
        }

        if (image instanceof HTMLImageElement || image instanceof HTMLCanvasElement || image instanceof ImageBitmap) {
            const canvas = document.createElement("canvas");
            canvas.width = image.width;
            canvas.height = image.height;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(image, 0, 0);
                return canvas.toDataURL()
            }
        }

        if (source && source.data instanceof HTMLImageElement) {
            const img = source.data as HTMLImageElement;
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(img, 0, 0);
                return canvas.toDataURL()
            }
        }

        return null;
    }

    private getDirectImageUrl(value: unknown): string | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const maybeImage = value as ImageSourceWithUrl;
        const directUrl = maybeImage.currentSrc || maybeImage.src;
        if (typeof directUrl === "string" && directUrl.length > 0) {
            return directUrl;
        }

        return null;
    }



    // Limit the maximum size to avoid huge memory consumption. These values are used when no width/height is specified in the options.
    private readonly maxDefaultTextureSize = 1024;
    private readonly maxDefaultTextureWidth = 1024;

    private _canvas: HTMLCanvasElement | null = null;
    private async renderTextureWithRenderer(texture: Texture | CompressedTexture | CubeTexture | WebGLRenderTarget, renderer: WebGLRenderer, options: ValueConverterOptions): Promise<string | null> {

        // renderer = shallowCloneObject(renderer);
        // renderer = new WebGLRenderer();

        if (isWebGLRenderTarget(texture)) {
            texture = texture.texture;
        }
        // PMREM/CubeUV environment atlases cannot be sampled as a plain 2D
        // map (node-based renderers sample them BLACK, and the raw atlas is
        // unreadable anyway) and cube textures aren't 2D at all — render a
        // view INTO the environment instead
        if (isEnvironmentProjection(texture)) {
            return this.renderEnvironmentWithRenderer(texture, renderer, options);
        }

        // three's Texture has NO width/height (only .image / .source.data /
        // compressed mipmaps do) — reading texture.width returns undefined, so
        // derive the source size the same way the interactive session does.
        const source = getTextureSourceSize(texture);
        let width = options.width || Math.min(this.maxDefaultTextureSize, source.width || Number.MAX_SAFE_INTEGER);
        let height = options.height || Math.min(this.maxDefaultTextureWidth, source.height || Number.MAX_SAFE_INTEGER);

        // clamp each axis independently to the source size and a hard GPU cap.
        // (The previous else-if chain could clamp only ONE axis — a 1x1 texture
        // requested at 110x110 produced a 1x110 preview, i.e. a huge vertical bar.)
        if (source.width > 0) width = Math.min(width, source.width);
        if (source.height > 0) height = Math.min(height, source.height);
        width = Math.max(1, Math.min(width, 4_096));
        height = Math.max(1, Math.min(height, 4_096));
        // preserve the source aspect: FIT into the requested box instead of
        // stretching to fill it (a 287x2000 label texture must stay pen-shaped;
        // the blit quad always fills the render target)
        if (source.width > 0 && source.height > 0) {
            const aspect = source.width / source.height;
            if (width / height > aspect) width = Math.max(1, Math.round(height * aspect));
            else height = Math.max(1, Math.round(width / aspect));
        }

        const originalRenderTarget = renderer.getRenderTarget();

        const renderTarget = new WebGLRenderTarget(width, height);
        const material = getMaterial();
        const geometry = new PlaneGeometry(2, 2);

        try {
            renderer.setRenderTarget(renderTarget);

            const camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
            camera.name = "TextureConverter Render Camera";
            camera.position.z = 1;

            const scene = new Scene();
            scene.name = "TextureConverter Render Scene";
            HierarchyNode.setFlags(scene, NodeFlags.HideInHierarchy);
            const mesh = new Mesh(geometry, material);
            mesh.name = "TextureConverter Render Mesh";
            // The fullscreen quad is always visible; host renderers may use custom culling APIs.
            mesh.frustumCulled = false;
            // flipY=false sources (glTF/DataTexture/compressed: first data row =
            // image top) sample upside down on the standard-UV blit plane —
            // mirror the QUAD (never the texture). The old code rewrote
            // repeat/offset around center to flip V, which pushed v out of
            // [0,1]; with the forced clamp that smeared compressed/WebGPU
            // textures into vertical stripes. Sampling the texture with its OWN
            // state keeps static previews matching the interactive session.
            // Render-target textures on a GL backend are exempt: also flipY=false
            // but bottom-up (row 0 = framebuffer bottom) → upright on a plain quad.
            // On a real WebGPU backend render targets are TOP-down, so they DO need
            // the mirror like any other flipY=false source (else upside-down).
            const isRenderTargetTexture = (texture as { isRenderTargetTexture?: boolean }).isRenderTargetTexture === true;
            const isWebGPU = (renderer as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend === true;
            const mirror = texture.flipY === false && (!isRenderTargetTexture || isWebGPU);
            mesh.scale.set(1, mirror ? -1 : 1, 1);
            scene.add(mesh);

            this.logger.debug("Rendering texture " + texture.type + " " + texture.name + " to blob url with size " + width + "x" + height);
            // WebGPU renderers need renderAsync so the draw is actually submitted
            // before the async pixel readback. A sync render() may not flush until
            // the next real frame, which — with on-demand rendering — can leave
            // readRenderTargetPixelsAsync pending for a very long time (or never).
            const maybeAsync = renderer as unknown as { renderAsync?: (s: unknown, c: unknown) => Promise<void> };
            if (typeof maybeAsync.renderAsync === "function") {
                await maybeAsync.renderAsync(scene, camera);
            } else {
                renderer.render(scene, camera);
            }
            // The render target is LINEAR and does no output encoding, so we
            // sRGB-encode the readback whenever its contents are linear:
            // - a texture tagged sRGB: MeshBasicMaterial linearizes the map on
            //   sample (sRGB→linear), so the RT holds linear — encode it back or
            //   the preview is too dark (correctly-tagged albedos).
            // - a texture tagged LINEAR-sRGB: three converts NOTHING on sample,
            //   so the RT holds the stored values — which that tag declares to BE
            //   linear. They still need encoding to be displayed, for a different
            //   reason than the sRGB case. Missing this showed a linear DataTexture
            //   skybox far darker/more saturated in the preview than on screen.
            // - an UNtagged (NoColorSpace) texture is sampled raw and its stored
            //   bytes are treated AS display values by convention → no encode.
            // - a render-target texture stores LINEAR (three encodes only the
            //   default framebuffer) unless the target itself is sRGB.
            // Render-target textures also display OPAQUE (RenderDoc convention,
            // matching the interactive session + frame-graph thumbnails):
            // composer buffers keep content in RGB with alpha 0, which previewed
            // as an empty checker.
            const encodeSRGB = isRenderTargetTexture
                ? texture.colorSpace !== SRGBColorSpace
                : texture.colorSpace !== NoColorSpace;
            // rows bottom-up: OUR quad render stores GL row order on every
            // backend (same rule as the preview session and the frame-graph
            // thumbnails — the old quad mirror used to cancel the missing flip
            // on WebGPU, which is why "auto" appeared to work here before).
            // forceOpaque "ifAlphaEmpty": composer buffers keep content in RGB
            // with alpha 0 (would preview as empty checker) — lift those; but a
            // render-target texture that genuinely uses alpha keeps its
            // transparency instead of going solid black.
            return await readbackRenderTargetToUrl(renderTarget, renderer, this.logger, { encodeSRGB, forceOpaque: isRenderTargetTexture ? "ifAlphaEmpty" : false, rows: "bottom-up" });
        }
        catch (error) {
            this.logger.error(`Failed to render texture ${texture.type} (${texture.name})`, error);
        }
        finally {
            renderer.setRenderTarget(originalRenderTarget);
            renderTarget.dispose();
            geometry.dispose();
            material.dispose();
        }


        return null;

        function getMaterial() {

            // transparent: opaque materials FORCE alpha to 1 in the shader
            // (#ifdef OPAQUE) — alpha-carrying textures would blit as solid
            // squares. NoBlending: blend-over-clear would premultiply the
            // color by alpha; we want the texture's straight values. DoubleSide:
            // the flipY=false mirror flips winding, so the quad's back face must
            // still render.
            return new MeshBasicMaterial({ map: texture, transparent: true, blending: NoBlending, side: DoubleSide });
            // let colorNode = texNode;
            // const texNode = texture(
            //     s,
            //     vec2(uvNode.x, sub(1, uvNode.y)),
            //     mipmapUniform,
            // );

        }
    }

    /**
     * Preview for prefiltered (PMREM/CubeUV) environment maps: an empty scene
     * with `scene.background = texture` and a wide perspective camera — a view
     * INTO the environment. three supports CubeUV backgrounds on both renderer
     * families; sampling the atlas as a plain map does NOT work (black on
     * node-based renderers) and would show the raw atlas at best.
     */
    private async renderEnvironmentWithRenderer(texture: Texture, renderer: WebGLRenderer, options: ValueConverterOptions): Promise<string | null> {
        const width = Math.max(1, Math.min(options.width || 512, 2_048));
        const height = Math.max(1, Math.min(options.height || 256, 2_048));

        const originalRenderTarget = renderer.getRenderTarget();
        const renderTarget = new WebGLRenderTarget(width, height);
        const scene = new Scene();
        scene.name = "TextureConverter Environment Scene";
        HierarchyNode.setFlags(scene, NodeFlags.HideInHierarchy);
        try {
            scene.background = texture;
            const camera = new PerspectiveCamera(90, width / height, 0.1, 10);
            camera.name = "TextureConverter Environment Camera";

            renderer.setRenderTarget(renderTarget);
            const maybeAsync = renderer as unknown as { renderAsync?: (s: unknown, c: unknown) => Promise<void> };
            if (typeof maybeAsync.renderAsync === "function") {
                await maybeAsync.renderAsync(scene, camera);
            } else {
                renderer.render(scene, camera);
            }
            // environment colors are LINEAR (HDR) — encode for display; tone
            // mapping / output encoding are skipped on render targets
            return await readbackRenderTargetToUrl(renderTarget, renderer, this.logger, { encodeSRGB: true });
        }
        catch (error) {
            this.logger.error(`Failed to render environment texture ${texture.type} (${texture.name})`, error);
        }
        finally {
            // never retain the host texture (weak-registry rule)
            scene.background = null;
            renderer.setRenderTarget(originalRenderTarget);
            renderTarget.dispose();
        }
        return null;
    }

    private renderCubeTextureToCanvas(
        cubeTexture: CubeTexture,
        options: ValueConverterOptions
    ): string | null {

        let faceSize = options.width || 512;
        if (faceSize > 1024) faceSize = 1024;
        if (cubeTexture.image && cubeTexture.image[0]) {
            const img = cubeTexture.image[0];
            if (img.width && img.height) {
                faceSize = Math.min(img.width, img.height, faceSize);
            }
        }

        const canvas = this._canvas ??= document.createElement("canvas");
        canvas.width = faceSize * 3;
        canvas.height = faceSize * 2;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        const facePositions = [
            { x: 2, y: 1 }, // PX (right)
            { x: 0, y: 1 }, // NX (left)
            { x: 1, y: 0 }, // PY (top)
            { x: 1, y: 2 }, // NY (bottom)
            { x: 1, y: 1 }, // PZ (front)
            { x: 3, y: 1 }, // NZ (back)
        ];

        try {
            for (let i = 0; i < 6; i++) {
                const faceImage = cubeTexture.image[i];
                if (!faceImage) continue;

                const pos = facePositions[i];
                ctx.drawImage(
                    faceImage,
                    pos.x * faceSize,
                    pos.y * faceSize,
                    faceSize,
                    faceSize
                );
            }

            return canvas.toDataURL();
        } catch (e) {
            console.error('CubeTexture rendering error:', e);
            return null;
        }
    }

    // // Apply exposure using uniform
    // // @ts-ignore
    // colorNode = vec4(
    //     colorNode.xyz.mul(pow(2.0, exposureUniform)),
    //     colorNode.w,
    // );

    // // Apply RGBA filtering based on uniform
    // // @ts-ignore
    // colorNode = select(
    //     equal(filterModeUniform, int(1)), // R
    //     vec4(colorNode.x, colorNode.x, colorNode.x, 1.0),
    //     select(
    //         equal(filterModeUniform, int(2)), // G
    //         vec4(colorNode.y, colorNode.y, colorNode.y, 1.0),
    //         select(
    //             equal(filterModeUniform, int(3)), // B
    //             vec4(colorNode.z, colorNode.z, colorNode.z, 1.0),
    //             select(
    //                 equal(filterModeUniform, int(4)), // A
    //                 vec4(
    //                     colorNode.w,
    //                     colorNode.w,
    //                     colorNode.w,
    //                     1.0,
    //                 ),
    //                 vec4(
    //                     colorNode.x,
    //                     colorNode.y,
    //                     colorNode.z,
    //                     colorNode.w,
    //                 ), // RGBA
    //             ),
    //         ),
    //     ),
    // );

    // // @ts-ignore
    // mat.colorNode = colorNode.xyz;
    // mat.opacityNode = colorNode.w;
}

// ValueConversionService.registerConverter(textureConverter);
